import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import twoFactorService from '../../services/twoFactorService';

// Async thunks
export const enable2FA = createAsyncThunk(
  'twoFactor/enable',
  async (data, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.enable(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to enable 2FA');
    }
  }
);

export const disable2FA = createAsyncThunk(
  'twoFactor/disable',
  async (_, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.disable();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to disable 2FA');
    }
  }
);

export const sendEmailOTP = createAsyncThunk(
  'twoFactor/sendEmailOTP',
  async (email, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.requestEmailOTP(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send email OTP');
    }
  }
);

export const sendSMSOTP = createAsyncThunk(
  'twoFactor/sendSMSOTP',
  async (phone_number, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.requestSMSOTP(phone_number);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to send SMS OTP');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'twoFactor/verifyOTP',
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.verifyOTP(email, otp);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Invalid OTP');
    }
  }
);

export const setupSecurityQuestions = createAsyncThunk(
  'twoFactor/setupSecurityQuestions',
  async (questions, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.setupSecurityQuestions(questions);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to setup security questions');
    }
  }
);

export const verifySecurityQuestions = createAsyncThunk(
  'twoFactor/verifySecurityQuestions',
  async ({ email, answers }, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.verifySecurityQuestions(email, answers);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Incorrect answers');
    }
  }
);

export const getSecurityQuestions = createAsyncThunk(
  'twoFactor/getSecurityQuestions',
  async (email, { rejectWithValue }) => {
    try {
      const response = await twoFactorService.getSecurityQuestions(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get security questions');
    }
  }
);

const twoFactorSlice = createSlice({
  name: 'twoFactor',
  initialState: {
    isEnabled: false,
    method: null, // 'email', 'sms', 'security_questions'
    securityQuestions: [],
    otpSent: false,
    otpExpiry: null,
    verified: false,
    loading: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetOTPState: (state) => {
      state.otpSent = false;
      state.otpExpiry = null;
      state.verified = false;
    },
    set2FAEnabled: (state, action) => {
      state.isEnabled = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Enable 2FA
      .addCase(enable2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(enable2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.isEnabled = true;
        state.method = action.payload.method;
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Disable 2FA
      .addCase(disable2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(disable2FA.fulfilled, (state) => {
        state.loading = false;
        state.isEnabled = false;
        state.method = null;
      })
      .addCase(disable2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Send Email OTP
      .addCase(sendEmailOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendEmailOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        state.otpExpiry = action.payload.expiry;
      })
      .addCase(sendEmailOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Send SMS OTP
      .addCase(sendSMSOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendSMSOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        state.otpExpiry = action.payload.expiry;
      })
      .addCase(sendSMSOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Verify OTP
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state) => {
        state.loading = false;
        state.verified = true;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Setup Security Questions
      .addCase(setupSecurityQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setupSecurityQuestions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(setupSecurityQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Verify Security Questions
      .addCase(verifySecurityQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifySecurityQuestions.fulfilled, (state) => {
        state.loading = false;
        state.verified = true;
      })
      .addCase(verifySecurityQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get Security Questions
      .addCase(getSecurityQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSecurityQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.securityQuestions = action.payload.questions || [];
      })
      .addCase(getSecurityQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, resetOTPState, set2FAEnabled } = twoFactorSlice.actions;
export default twoFactorSlice.reducer;
